import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import LogoLight from "../assets/images/logo-light.png";
import LogoDark from "../assets/images/logo-dark.png";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";

export default function Navbar({ navdark }) {
  const [isOpen, setMenu] = useState(true);
  const [activeSection, setActiveSection] = useState('');
  const { t } = useTranslation();

  useEffect(() => {
    // Order must match actual DOM position, not nav order
    const sectionIds = ['home', 'how-it-works', 'product', 'industries', 'about', 'contact'];

    function onScroll() {
      const navbar = document.getElementById("navbar");
      const heroHeight = window.innerHeight;
      if (document.documentElement.scrollTop >= heroHeight - 80) {
        navbar.classList.add("is-sticky");
      } else {
        navbar.classList.remove("is-sticky");
      }

      const scrollY = window.scrollY + 120;
      let current = '';
      for (const id of sectionIds) {
        const el = document.getElementById(id);
        if (el && el.offsetTop <= scrollY) current = id;
      }
      setActiveSection(current);
    }

    window.addEventListener("scroll", onScroll, { passive: true });
    window.scrollTo(0, 0);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const toggleMenu = () => setMenu(!isOpen);

  const scrollToTop = () => {
    if (window.location.pathname === "/" || window.location.pathname === "/index") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <>
      <nav className="navbar flex" id="navbar">
        <div className="container flex flex-wrap items-center">
          {/* Logo — always on the left */}
          {navdark ? (
            <Link className="navbar-brand" to="/" onClick={scrollToTop}>
              <span>
                <img src={LogoDark} className="inline-block dark:hidden" alt="ANA Labs" />
                <img src={LogoLight} className="hidden dark:inline-block" alt="ANA Labs" />
              </span>
            </Link>
          ) : (
            <Link className="navbar-brand" to="/" onClick={scrollToTop}>
              <span className="inline-block dark:hidden">
                <img src={LogoDark} className="l-dark" alt="ANA Labs" />
                <img src={LogoLight} className="l-light" alt="ANA Labs" />
              </span>
              <img src={LogoLight} className="hidden dark:inline-block" alt="ANA Labs" />
            </Link>
          )}

          {/* Hamburger: ms-auto on mobile pushes it right; reset on desktop */}
          <div className="nav-icons flex items-center lg_992:order-2 ms-auto lg_992:ms-0">
            <button
              type="button"
              className="collapse-btn inline-flex items-center ms-3 text-dark dark:text-white lg_992:hidden"
              onClick={toggleMenu}
            >
              <span className="sr-only">Navigation Menu</span>
              <i className="mdi mdi-menu mdi-24px"></i>
            </button>
          </div>

          {/* Nav links: ms-auto on desktop pushes the whole block to the right */}
          <div
            className={`${isOpen === true ? "hidden" : "block"} navigation lg_992:order-1 lg_992:flex lg_992:ms-auto`}
            id="menu-collapse"
          >
            <ul className={`navbar-nav ${navdark ? "" : "nav-light"}`} id="navbar-navlist">
              <Link className="nav-item" to="/#product">
                <span className={`nav-link ${activeSection === 'product' ? "nav-active" : ""}`}>
                  {t("NavBar.product")}
                </span>
              </Link>

              <Link className="nav-item" to="/#industries">
                <span className={`nav-link ${activeSection === 'industries' ? "nav-active" : ""}`}>
                  {t("NavBar.useCases")}
                </span>
              </Link>

              <Link className="nav-item" to="/#how-it-works">
                <span className={`nav-link ${activeSection === 'how-it-works' ? "nav-active" : ""}`}>
                  {t("NavBar.howItWorks")}
                </span>
              </Link>

              <Link className="nav-item" to="/#about">
                <span className={`nav-link ${activeSection === 'about' ? "nav-active" : ""}`}>
                  {t("NavBar.company")}
                </span>
              </Link>

              <Link className="nav-item" to="/#contact">
                <span className={`nav-link ${activeSection === 'contact' ? "nav-active" : ""}`}>
                  {t("NavBar.contact")}
                </span>
              </Link>

              <li className="hidden lg_992:inline-flex items-center ms-2">
                <LanguageSwitcher />
              </li>

              {/* Desktop CTA — uses accent color to stand out on both dark hero and sticky white navbar */}
              <li className="hidden lg_992:inline-flex items-center ms-3">
                <Link
                  to="/#contact"
                  className="rounded-lg text-sm py-2 px-5 font-semibold transition-all duration-200 hover:opacity-90"
                  style={{ background: '#00C9B1', color: '#010C66', fontFamily: '"Inter", sans-serif' }}
                >
                  {t("CTA.requestDemo")}
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </nav>
    </>
  );
}
